import React from "react";
import { defineMessages, useIntl } from "react-intl";
import classnames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { BodyParagraph } from "theme/components/atoms/Typography/Body";
import Button from "theme/components/atoms/Button";
import Link from "theme/components/atoms/Typography/Link";
import H3 from "theme/components/atoms/Typography/Heading/H3";
import { H4 } from "theme/components/atoms/Typography/Heading";
import PropTypes from "prop-types";

const messages = defineMessages({
  deny: {
    id: "modules.Cookie.CookiePage.CookieLine.deny",
    defaultMessage: "Deny",
  },
  allow: {
    id: "modules.Cookie.CookiePage.CookieLine.allow",
    defaultMessage: "Allow",
  },

  viewSite: {
    id: "modules.Cookie.CookiePage.CookieLine.viewSite",
    defaultMessage: "View site",
  },
  readMore: {
    id: "modules.Cookie.CookiePage.CookieLine.readMore",
    defaultMessage: "Read more",
  },
});

const CookieLine = ({
  service,
  status,
  consentCookie,
  updateConsent,
  updateCookie,
}) => {
  const intl = useIntl();
  const [showReadMore, setShowReadMore] = useState(false);
  const [inspectConsentOptions, setInspectConsentOptions] = useState(false);

  const handleSetStatus = useCallback(
    (newStatus) => {
      if (status !== null && newStatus !== status) {
        updateCookie(newStatus);
      }
    },
    [status, updateCookie]
  );

  const handleSetConsent = useCallback(
    (name, newConsentStatus) => {
      if (consentCookie?.[name] !== newConsentStatus) {
        updateConsent(name, newConsentStatus);
      }
      if (!status && newConsentStatus) {
        handleSetStatus(true);
      }
    },
    [status, consentCookie, handleSetStatus, updateConsent]
  );

  const hasConsentOptions = Boolean(service.consentOptions?.length);
  const consentOptionsWithStatus = useMemo(
    () =>
      service.consentOptions?.map((option) => {
        const consentOptionStatus =
          consentCookie && option.name in consentCookie
            ? consentCookie?.[option.name] === true && status === true
            : status === true;

        return {
          ...option,
          status: consentOptionStatus,
        };
      }) || [],
    [service.consentOptions, consentCookie, status]
  );
  const hasActiveConsentOption = consentOptionsWithStatus.some(
    (option) => option.status === true
  );
  const showConsentOptions =
    inspectConsentOptions || status === true || hasActiveConsentOption;

  return (
    <div
      className={classnames("cookie-line", {
        "cookie-line--configured": status !== undefined,
      })}
    >
      <div className="cookie-line__info">
        <H3>{service.title}</H3>
        <div className="cookie-line__info__description">
          {service.description && (
            <Button
              appearance="link"
              onClick={() => setShowReadMore((curr) => !curr)}
            >
              {intl.formatMessage(messages.readMore)}
            </Button>
          )}

          {hasConsentOptions && status !== true && (
            <Button
              appearance="link"
              onClick={() => setInspectConsentOptions((curr) => !curr)}
            >
              Show Consent
            </Button>
          )}

          {service.link && (
            <Link to={service.link} external>
              {intl.formatMessage(messages.viewSite)}
            </Link>
          )}
          {showReadMore && <BodyParagraph>{service.description}</BodyParagraph>}
        </div>
      </div>
      <div className="cookie-line__actions">
        <div>
          <Button
            appearance={status === true ? "primary" : undefined}
            onClick={() => handleSetStatus(true)}
          >
            {intl.formatMessage(messages.allow)}
          </Button>
        </div>
        <div>
          <Button
            appearance={status === false ? "primary" : undefined}
            onClick={() => handleSetStatus(false)}
          >
            {intl.formatMessage(messages.deny)}
          </Button>
        </div>
      </div>

      {hasConsentOptions && showConsentOptions && (
        <div className="cookie-line__consent-options">
          {consentOptionsWithStatus?.map((option) => {
            return (
              <div
                className="cookie-line__consent-options-line"
                key={option.name}
              >
                <div className="cookie-line__info">
                  <H4>{option.title}</H4>
                  <div className="cookie-line__info__description">
                    <BodyParagraph>{option.description}</BodyParagraph>
                  </div>
                </div>
                <div className="cookie-line__actions">
                  <div>
                    <Button
                      appearance={
                        option.status === true ? "primary" : undefined
                      }
                      onClick={() => handleSetConsent(option.name, true)}
                    >
                      {intl.formatMessage(messages.allow)}
                    </Button>
                  </div>
                  <div>
                    <Button
                      appearance={
                        option.status === false ? "primary" : undefined
                      }
                      onClick={() => handleSetConsent(option.name, false)}
                    >
                      {intl.formatMessage(messages.deny)}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

CookieLine.propTypes = {
  service: PropTypes.shape({
    title: PropTypes.string.isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
  }),
};

export default CookieLine;
